import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  IconButton,
  Tooltip,
  Flex,
  Spacer,
  Divider,
  Spinner,
  Textarea,
  Center,
  Heading,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Icon,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { FiPlus, FiTrash, FiSearch, FiUploadCloud, FiFileText, FiDownload, FiMoreVertical } from 'react-icons/fi';

// Dummy data structures to simulate Retell API responses
const dummyKnowledgeBases = [
  { id: 'kb_1', name: 'Voice FAQs' },
  { id: 'kb_2', name: 'Agent Script' },
  { id: 'kb_3', name: 'Product Info' },
];

const dummySources = {
  kb_1: [
    { id: 'src_1', filename: 'voice_faq.pdf', type: 'file', tags: ['FAQ', 'Voice'] },
    { id: 'src_2', content: 'How to use voice commands...', type: 'text', tags: ['How-to'] },
  ],
  kb_2: [
    { id: 'src_3', filename: 'script_v1.docx', type: 'file', tags: ['Script', 'Version 1'] },
  ],
  kb_3: [
    { id: 'src_4', content: 'Latest product features:...', type: 'text', tags: ['Features'] },
    { id: 'src_5', filename: 'product_sheet.csv', type: 'file', tags: ['Data'] },
  ],
};

const VoiceAgentKnowledgeBase = () => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newKbName, setNewKbName] = useState('');
  const [newSourceType, setNewSourceType] = useState('file'); // 'file' or 'text'
  const [newSourceFile, setNewSourceFile] = useState(null);
  const [newSourceText, setNewSourceText] = useState('');
  const [newSourceTags, setNewSourceTags] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [textSource, setTextSource] = useState('');
  const [loading, setLoading] = useState(false);

  const { isOpen: isKbModalOpen, onOpen: onKbModalOpen, onClose: onKbModalClose } = useDisclosure();
  const { isOpen: isSourceModalOpen, onOpen: onSourceModalOpen, onClose: onSourceModalClose } = useDisclosure();
  const { isOpen: isAddDocumentModalOpen, onOpen: onAddDocumentModalOpen, onClose: onAddDocumentModalClose } = useDisclosure();

  const toast = useToast();

  // Simulate fetching knowledge bases
  useEffect(() => {
    setIsLoading(true);
    // In a real app, you would call Retell API here (List Knowledge Bases)
    setTimeout(() => {
      setKnowledgeBases(dummyKnowledgeBases);
      setIsLoading(false);
    }, 500);
  }, []);

  // Simulate fetching sources for selected KB
  useEffect(() => {
    if (selectedKnowledgeBase) {
      setIsLoading(true);
      // In a real app, you would call Retell API here (Get Knowledge Base or List Sources endpoint if available)
      setTimeout(() => {
        setSources(dummySources[selectedKnowledgeBase.id] || []);
        setIsLoading(false);
      }, 500);
    } else {
      setSources([]);
    }
  }, [selectedKnowledgeBase]);

  const handleCreateKnowledgeBase = () => {
    if (!newKbName.trim()) {
      toast({ title: 'Knowledge Base name cannot be empty.', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    setIsLoading(true);
    // In a real app, you would call Retell API here (Create Knowledge Base)
    setTimeout(() => {
      const newKb = { id: `kb_${Date.now()}`, name: newKbName };
      setKnowledgeBases([...knowledgeBases, newKb]);
      setNewKbName('');
      onKbModalClose();
      toast({ title: 'Knowledge Base created.', status: 'success', duration: 3000, isClosable: true });
      setIsLoading(false);
    }, 500);
  };

  const handleDeleteKnowledgeBase = (kbId) => {
    setIsLoading(true);
    // In a real app, you would call Retell API here (Delete Knowledge Base)
    setTimeout(() => {
      setKnowledgeBases(knowledgeBases.filter(kb => kb.id !== kbId));
      if (selectedKnowledgeBase && selectedKnowledgeBase.id === kbId) {
        setSelectedKnowledgeBase(null);
        setSources([]);
      }
      toast({ title: 'Knowledge Base deleted.', status: 'success', duration: 3000, isClosable: true });
      setIsLoading(false);
    }, 500);
  };

  const handleAddSource = () => {
    if (selectedKnowledgeBase) {
      setIsLoading(true);
      // In a real app, you would call Retell API here (Add Knowledge Base Sources)
      setTimeout(() => {
        const newSource = {
          id: `src_${Date.now()}`,
          type: newSourceType,
          filename: newSourceType === 'file' ? (newSourceFile ? newSourceFile.name : 'uploaded_file') : null,
          content: newSourceType === 'text' ? newSourceText : null,
          tags: newSourceTags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
        };
        // Update dummySources structure (less critical for UI-only)
        if (!dummySources[selectedKnowledgeBase.id]) {
          dummySources[selectedKnowledgeBase.id] = [];
        }
        dummySources[selectedKnowledgeBase.id].push(newSource);

        setSources([...sources, newSource]);
        setNewSourceFile(null);
        setNewSourceText('');
        setNewSourceTags('');
        onSourceModalClose();
        toast({ title: 'Source added.', status: 'success', duration: 3000, isClosable: true });
        setIsLoading(false);
      }, 500);
    }
  };

  const handleDeleteSource = (sourceId) => {
    if (selectedKnowledgeBase) {
      setIsLoading(true);
      // In a real app, you would call Retell API here (Delete Knowledge Base Source)
      setTimeout(() => {
        setSources(sources.filter(source => source.id !== sourceId));
        // Update dummySources structure (less critical for UI-only)
        if (dummySources[selectedKnowledgeBase.id]) {
           dummySources[selectedKnowledgeBase.id] = dummySources[selectedKnowledgeBase.id].filter(source => source.id !== sourceId);
        }
        toast({ title: 'Source deleted.', status: 'success', duration: 3000, isClosable: true });
        setIsLoading(false);
      }, 500);
    }
  };

  const handleExportData = () => {
    if (!selectedKnowledgeBase) {
      toast({
        title: 'No knowledge base selected',
        description: 'Please select a knowledge base to export its data.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const exportData = {
      knowledgeBase: selectedKnowledgeBase,
      sources: sources,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedKnowledgeBase.name.toLowerCase().replace(/\s+/g, '-')}-export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Data exported successfully',
      description: `Knowledge base "${selectedKnowledgeBase.name}" data has been exported.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      onAddDocumentModalOpen();
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  const addDocuments = async () => {
    if (selectedFiles.length === 0 && !textSource) {
      toast({
        title: 'No files selected or text entered',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: 'Document(s) Added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setSelectedFiles([]);
      setTextSource('');
      onAddDocumentModalClose();
    } catch (error) {
      toast({
        title: 'Error Adding Documents',
        description: error.message || 'Failed to add documents',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="4xl" mx="auto" p={6} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" size="lg" mb={4}>
        Upload Knowledge File
      </Heading>
      <Divider mb={4} />

      <FormControl>
        <input
          type="file"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="upload-kb-file"
          accept=".pdf,.txt,.doc,.docx,.xls,.xlsx"
        />
        <FormLabel htmlFor="upload-kb-file" mb="0">
          <Button as="span" colorScheme="orange" cursor="pointer">
            <HStack>
              <Icon as={FiUploadCloud} />
              <Text>Upload Knowledge File</Text>
            </HStack>
          </Button>
        </FormLabel>
      </FormControl>

      <Modal isOpen={isAddDocumentModalOpen} onClose={onAddDocumentModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch" pb={4}>
              <Text fontWeight="bold">Upload Files</Text>
              <FormControl>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.txt,.doc,.docx,.xls,.xlsx"
                />
              </FormControl>
              {selectedFiles.length > 0 && (
                <Text fontSize="sm" color="gray.600">{selectedFiles.length} file(s) selected</Text>
              )}

              <Text fontWeight="bold" mt={4}>Or Add Text</Text>
              <FormControl>
                <Textarea
                  value={textSource}
                  onChange={(e) => setTextSource(e.target.value)}
                  placeholder="Enter text content..."
                  rows={6}
                />
              </FormControl>
              <Button 
                colorScheme="blue" 
                onClick={addDocuments} 
                isLoading={loading}
                isDisabled={selectedFiles.length === 0 && !textSource}
              >
                Add Document(s)
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VoiceAgentKnowledgeBase; 